import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Home, Sparkles, Eye, Brain, Heart, Target, Moon, Gem, Wand2, Compass, BookOpen, MessageCircle, TrendingUp, Shield, Zap, Activity, Hand, Users, TreePine, Circle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { PageHeader } from '@/components/PageHeader';
import { User } from '@supabase/supabase-js';
import ClairsentientJournal from '@/components/telempathy/ClairsentientJournal';
import SynchronicityTracker from '@/components/telempathy/SynchronicityTracker';
import DreamJournal from '@/components/telempathy/DreamJournal';
import AutomaticWriting from '@/components/telempathy/AutomaticWriting';
import MediumshipJournal from '@/components/telempathy/MediumshipJournal';
import SpiritGuideLog from '@/components/telempathy/SpiritGuideLog';
import EnergyBodyTracker from '@/components/telempathy/EnergyBodyTracker';
import PsychicProtection from '@/components/telempathy/PsychicProtection';
import AccuracyTracker from '@/components/telempathy/AccuracyTracker';
import VibrationalFrequency from '@/components/telempathy/VibrationalFrequency';
import PsychometryLog from '@/components/telempathy/PsychometryLog';
import ThirdEyeDevelopment from '@/components/telempathy/ThirdEyeDevelopment';
import TarotLearningHub from '@/components/telempathy/TarotLearningHub';
import SoulBot from '@/components/telempathy/SoulBot';
import PsychicPracticeLab from '@/components/telempathy/PsychicPracticeLab';
import SpiritConnectionHub from '@/components/telempathy/SpiritConnectionHub';
import BalancedGuidance from '@/components/telempathy/BalancedGuidance';
import IntuitionDevelopment from '@/components/telempathy/IntuitionDevelopment';
import ConnectedSelf from '@/components/ConnectedSelf';
import ChakraHealingModule from '@/components/telempathy/ChakraHealingModule';
import { useEditableContent } from '@/hooks/useEditableContent';
import MirrorOfMeaning from '@/components/oracle/MirrorOfMeaning';
import { OracleCardsFan } from '@/components/OracleCardsFan';

export default function TelempathyTools() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('psychic');
  const [activeTool, setActiveTool] = useState('');
  const [user, setUser] = useState<User | null>(null);
  
  // Editable content
  const pageTitle = useEditableContent('telempathy.page.title', 'Telempathy Development Suite', 'Telempathy');
  const pageDescription = useEditableContent('telempathy.page.description', 'A comprehensive toolkit to develop your psychic, intuitive, and mediumship abilities', 'Telempathy');
  const dailyGuidanceTitle = useEditableContent('dashboard.daily_guidance_title', 'Daily Guidance', 'dashboard', 'Daily guidance section title');
  const dailyGuidanceDescription = useEditableContent('dashboard.daily_guidance_description', 'Swipe through the multi-dimensional oracle deck and let your intuition guide you to today\'s message.', 'dashboard', 'Daily guidance description');

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
    });
    
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  const tools = [
    // Psychic Development
    {
      id: 'tarot-learning',
      title: 'Tarot Learning Hub',
      description: 'Master Rider-Waite tarot with daily practice & gamification',
      icon: BookOpen,
      category: 'psychic',
      component: TarotLearningHub,
      color: 'from-[#4ba2a4] to-[#aad8d6]'
    },
    {
      id: 'third-eye',
      title: 'Third Eye Development',
      description: 'Clairvoyance exercises & progress tracking',
      icon: Eye,
      category: 'psychic',
      component: ThirdEyeDevelopment,
      color: 'from-[#aad8d6] to-[#4ba2a4]'
    },
    {
      id: 'accuracy',
      title: 'Accuracy Tracker',
      description: 'Build confidence by tracking hits vs misses',
      icon: Target,
      category: 'psychic',
      component: AccuracyTracker,
      color: 'from-[#4ba2a4]/80 to-[#4ba2a4]'
    },
    {
      id: 'vibration',
      title: 'Vibrational Frequency',
      description: 'Track your energetic state & reading quality',
      icon: Activity,
      category: 'psychic',
      component: VibrationalFrequency,
      color: 'from-[#4ba2a4] to-[#aad8d6]/80'
    },
    {
      id: 'practice-lab',
      title: 'Psychic Practice Lab',
      description: 'Structured exercises to develop all your clairs and track progress',
      icon: Zap,
      category: 'psychic',
      component: PsychicPracticeLab,
      color: 'from-[#4ba2a4] to-[#aad8d6]/80'
    },
    {
      id: 'spirit-connection',
      title: 'Spirit Connection Hub',
      description: 'Request signs, track ADC experiences, and connect with loved ones in spirit',
      icon: Heart,
      category: 'mediumship',
      component: SpiritConnectionHub,
      color: 'from-[#aad8d6]/80 to-[#4ba2a4]/80'
    },
    // Intuitive Skills
    {
      id: 'clairsentience',
      title: 'Clairsentience Journal',
      description: 'Track psychic impressions & intuitive hits',
      icon: Heart,
      category: 'intuitive',
      component: ClairsentientJournal,
      color: 'from-[#4ba2a4] to-[#aad8d6]'
    },
    {
      id: 'synchronicity',
      title: 'Synchronicity Tracker',
      description: 'Log meaningful coincidences & universal messages',
      icon: Sparkles,
      category: 'intuitive',
      component: SynchronicityTracker,
      color: 'from-[#aad8d6] to-[#4ba2a4]'
    },
    {
      id: 'dreams',
      title: 'Dream Symbolism',
      description: 'Record dreams & identify archetypal symbols',
      icon: Moon,
      category: 'intuitive',
      component: DreamJournal,
      color: 'from-[#4ba2a4]/70 to-[#aad8d6]'
    },
    {
      id: 'psychometry',
      title: 'Psychometry Log',
      description: 'Practice reading energy from objects',
      icon: Hand,
      category: 'intuitive',
      component: PsychometryLog,
      color: 'from-[#aad8d6]/80 to-[#4ba2a4]/80'
    },
    {
      id: 'energy-body',
      title: 'Energy Body Tracker',
      description: 'Monitor chakras & subtle energy sensations',
      icon: Activity,
      category: 'intuitive',
      component: EnergyBodyTracker,
      color: 'from-[#4ba2a4]/90 to-[#aad8d6]/70'
    },
    {
      id: 'chakra-healing',
      title: 'Chakra Healing',
      description: 'Work with your energy centres in a grounded way',
      icon: Circle,
      category: 'intuitive',
      component: ChakraHealingModule,
      color: 'from-purple-500/80 to-red-500/80'
    },
    {
      id: 'rock-remedies',
      title: 'Rock Remedies®',
      description: 'Crystal healing, moon ceremonies & grounding rituals',
      icon: Gem,
      category: 'intuitive',
      isLink: true,
      route: '/rock-remedies',
      color: 'from-violet-500/80 to-pink-500/80'
    },
    {
      id: 'intuition-development',
      title: 'Intuition Development',
      description: 'Train your physical senses & connect with nature to enhance psychic abilities',
      icon: TreePine,
      category: 'intuitive',
      component: IntuitionDevelopment,
      color: 'from-[#aad8d6]/80 to-[#4ba2a4]/80'
    },
    {
      id: 'balanced-guidance',
      title: 'Balanced Self-Guidance',
      description: 'Connect mind, heart, intuition & spirit for balanced decision-making',
      icon: Brain,
      category: 'intuitive',
      component: BalancedGuidance,
      color: 'from-[#4ba2a4]/80 to-[#aad8d6]/80'
    },
    // Mediumship
    {
      id: 'mediumship',
      title: 'Mediumship Practice',
      description: 'Log evidential information & validations',
      icon: Gem,
      category: 'mediumship',
      component: MediumshipJournal,
      color: 'from-[#aad8d6] to-[#4ba2a4]'
    },
    {
      id: 'spirit-guide',
      title: 'Spirit Guide Comm',
      description: 'Record messages & signs from guides',
      icon: Compass,
      category: 'mediumship',
      component: SpiritGuideLog,
      color: 'from-[#4ba2a4] to-[#aad8d6]'
    },
    {
      id: 'automatic-writing',
      title: 'Automatic Writing',
      description: 'Channel guidance through free-flow writing',
      icon: Wand2,
      category: 'mediumship',
      component: AutomaticWriting,
      color: 'from-[#4ba2a4]/85 to-[#aad8d6]/85'
    },
    {
      id: 'psychic-protection',
      title: 'Psychic Protection',
      description: 'Learn & practice energetic boundaries',
      icon: Shield,
      category: 'mediumship',
      component: PsychicProtection,
      color: 'from-[#aad8d6]/90 to-[#4ba2a4]'
    },
    {
      id: 'connected-self',
      title: 'Connected Self',
      description: 'Balance all levels: self, client & spirit connection',
      icon: Users,
      category: 'mediumship',
      component: ConnectedSelf,
      color: 'from-[#4ba2a4]/75 to-[#aad8d6]/75'
    }
  ];

  const soulbotContexts = {
    psychic: {
      title: 'Soul Guidance: Interpret Your Psychic Impressions',
      description: 'Use AI to understand and validate your psychic readings'
    },
    intuitive: {
      title: 'Soul Guidance: Decode Your Dream Symbolism',
      description: 'Get guidance on interpreting intuitive messages and synchronicities'
    },
    mediumship: {
      title: 'Soul Guidance: Messages from Your Higher Self',
      description: 'Receive spiritual interpretation and guidance from spirit'
    }
  };

  const categories = {
    psychic: { 
      name: 'Psychic Development', 
      icon: Brain,
      description: 'Build & strengthen your psychic abilities with structured practice'
    },
    intuitive: { 
      name: 'Intuitive Skills', 
      icon: Heart,
      description: 'Develop your natural intuition & inner knowing'
    },
    mediumship: { 
      name: 'Mediumship & Connection', 
      icon: Sparkles,
      description: 'Connect with spirit guides & higher consciousness'
    }
  };

  const getToolsByCategory = (category: string) => {
    return tools.filter(tool => tool.category === category);
  };

  const ActiveComponent = tools.find(t => t.id === activeTool)?.component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-[#4ba2a4]/5 to-[#aad8d6]/10">
      <PageHeader title="Telempathy Tools" />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Hero Header */}
        <div className="text-center space-y-4 py-8">
          <h1 className="text-5xl md:text-6xl font-light tracking-tight text-foreground animate-fade-in drop-shadow-lg">
            {pageTitle}
          </h1>
          <p className="text-lg text-foreground/80 max-w-2xl mx-auto font-light">
            {pageDescription}
          </p>
        </div>

        {/* Oracle Cards Section - Daily Guidance */}
        <Card className="mb-6 sm:mb-8 p-4 sm:p-6 bg-gradient-to-br from-[#4ba2a4]/10 to-[#aad8d6]/10 shadow-xl border-2 border-[#4ba2a4]/20">
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="h-6 w-6 text-primary" />
              <h3 className="text-2xl font-light text-foreground">{dailyGuidanceTitle}</h3>
              <Sparkles className="h-6 w-6 text-primary" />
            </div>
            <p className="text-muted-foreground">{dailyGuidanceDescription}</p>
          </div>
          
          <OracleCardsFan />
        </Card>


        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
          <TabsList className="grid w-full grid-cols-3 h-auto gap-2 sm:gap-3 bg-card/50 backdrop-blur-sm p-2">
            {Object.entries(categories).map(([key, cat]) => {
              const Icon = cat.icon;
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="flex flex-col items-center justify-center gap-1 sm:gap-2 py-3 sm:py-4 px-2 sm:px-4 min-h-[80px] sm:min-h-[90px] border-2 border-[#aad8d6]/40 text-[#F6F3F0] bg-[#2F5354]/20 hover:bg-[#2F5354]/40 hover:border-[#aad8d6]/60 data-[state=active]:bg-gradient-to-br data-[state=active]:from-[#4ba2a4]/30 data-[state=active]:to-[#aad8d6]/30 data-[state=active]:border-[#aad8d6] data-[state=active]:text-[#F6F3F0] rounded-lg transition-all duration-300 hover:scale-105"
                >
                  <Icon className="h-5 w-5 sm:h-6 sm:w-6 shrink-0" style={{ color: key === activeTab ? '#CBB281' : 'hsl(var(--foreground))' }} />
                  <span className="text-[10px] sm:text-xs font-light tracking-wide text-center leading-tight break-words hyphens-auto">{cat.name}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {Object.entries(categories).map(([category, catInfo]) => (
            <TabsContent key={category} value={category} className="space-y-8 animate-fade-in">
              {/* Tools Grid */}
              <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {getToolsByCategory(category).map((tool) => {
                  const Icon = tool.icon;
                  
                  return (
                    <Card
                      key={tool.id}
                      className="group cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-2xl border-2 hover:border-[#4ba2a4]/50 overflow-hidden"
                      onClick={() => {
                        if ((tool as any).isLink && (tool as any).route) {
                          navigate((tool as any).route);
                        } else {
                          setActiveTool(tool.id);
                        }
                      }}
                    >
                      <div className={`h-2 bg-gradient-to-r ${tool.color} animate-shimmer`} />
                      <CardContent className="p-6 text-center space-y-3">
                        <div className={`mx-auto w-16 h-16 rounded-full bg-gradient-to-br ${tool.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                          <Icon className="h-8 w-8 text-white" />
                        </div>
                        <h3 className="font-light text-base tracking-wide text-foreground">{tool.title}</h3>
                        <p className="text-xs text-foreground/70 font-light leading-relaxed">{tool.description}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* SoulBot Button - contextual to category */}
              <Card className="border-2 border-[#4ba2a4]/30 bg-gradient-to-br from-[#4ba2a4]/5 to-[#aad8d6]/5 hover:shadow-xl transition-all duration-300 cursor-pointer hover:scale-[1.02]"
                onClick={() => setActiveTool('soulbot-' + category)}
              >
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#4ba2a4] to-[#aad8d6] flex items-center justify-center shadow-lg">
                      <MessageCircle className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <h3 className="font-light text-lg tracking-wide text-foreground">
                        {soulbotContexts[category as keyof typeof soulbotContexts].title}
                      </h3>
                      <p className="text-sm text-foreground/70 font-light mt-1">
                        {soulbotContexts[category as keyof typeof soulbotContexts].description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Dialog for Active Tool */}
      <Dialog open={!!activeTool} onOpenChange={(open) => !open && setActiveTool('')}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-2xl font-light tracking-wide">
              {activeTool.startsWith('soulbot-') ? (
                <>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#4ba2a4] to-[#aad8d6] flex items-center justify-center">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-foreground">Soul Guidance AI</span>
                </>
              ) : (
                <>
                  {tools.find(t => t.id === activeTool) && (
                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${tools.find(t => t.id === activeTool)?.color} flex items-center justify-center`}>
                      {React.createElement(tools.find(t => t.id === activeTool)?.icon || Sparkles, {
                        className: "h-5 w-5 text-white"
                      })}
                    </div>
                  )}
                  <span>{tools.find(t => t.id === activeTool)?.title}</span>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {activeTool.startsWith('soulbot-') ? (
              <SoulBot />
            ) : (
              ActiveComponent && <ActiveComponent onDataChange={() => {}} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
